import axios from 'axios';
import type {
  CreateReportCoverImageRequest,
  CreateReportCoverImageResponse,
} from '@shared/models/report-cover-image.model';
import {
  getReportCoverImagePath,
  isReportCoverImageMissingStatus,
} from '@shared/helpers/report-cover-image.helpers';

type CoverImagePathInput = {
  projectId: number;
  generationKey?: string;
  sharingKey?: string;
};

/**
 * Fetch the persisted photorealistic report cover as a blob.
 * Returns `undefined` when none is stored ({@link isReportCoverImageMissingStatus} / HTTP 204).
 * Other statuses (including 404) reject and must be treated as a failed request, not absence.
 */
export const fetchReportCoverImage = async (
  input: CoverImagePathInput,
): Promise<Blob | undefined> => {
  const { data, status } = await axios.get<Blob>(
    getReportCoverImagePath(input),
    {
      responseType: 'blob',
      validateStatus: (httpStatus) =>
        httpStatus === 200 || isReportCoverImageMissingStatus(httpStatus),
    },
  );
  if (isReportCoverImageMissingStatus(status)) return undefined;
  return data;
};

/**
 * Persist a photorealistic cover image generated via `/design-ai/renderings`.
 */
export const createReportCoverImage = async (
  projectId: number,
  body: CreateReportCoverImageRequest,
): Promise<CreateReportCoverImageResponse> => {
  const { data } = await axios.post<CreateReportCoverImageResponse>(
    `projects/${projectId}/report-cover-image`,
    body,
  );
  return data;
};

/**
 * Delete the persisted report cover image.
 */
export const deleteReportCoverImage = async (
  projectId: number,
): Promise<void> => {
  await axios.delete(`projects/${projectId}/report-cover-image`);
};
